// Note: This interceptor could be trivially combined with the
//       bff interceptor. However, they are being kept deliberately
//       separate despite the obvious duplicate code

// EXPERIMENT: create a separate AuthError interceptor to replace the
// messy (untested) plumbing that I put in here
// See also: README.md#http-interceptor-architecture-hybrid

import { HttpErrorResponse, HttpRequest, HttpHandler, HttpInterceptor, HttpEvent } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthRedirectService } from 'src/app/shared/services/auth-redirect.service';


@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  // Purpose:
  // - Normalize legacy API-style routes so they comply with BFF cookie/CSRF
  //   requirements from Angular HttpClient.
  // - Provide a local 401 redirect safety net for these routes.
  private authRedirect: AuthRedirectService | null = null;

  constructor(private injector: Injector) {
    // Delay injection of AuthRedirectService via Injector to avoid potential
    // circular dependency at app bootstrap.
    try {
      this.authRedirect = this.injector.get(AuthRedirectService);
    } catch {
      // Ignore DI failures; fallback to direct redirect if needed.
      this.authRedirect = null;
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Match all API-family paths that are expected to flow through BFF policy.
    // These prefixes represent migrated and non-migrated backend route groups.
    if (this.isApiFamilyRequest(req.url)) {
      const headers = req.headers.has('X-CSRF')
        ? req.headers
        : req.headers.set('X-CSRF', '1');

      const httpsReq = req.clone({
        // Ensure authentication cookie + CSRF marker are always present.
        withCredentials: true,
        headers
      });

      return next.handle(httpsReq)
        .pipe(
          catchError((error: HttpErrorResponse) => {

            if (error.status === 401 && error.url?.includes('/bff/user')) {
              // Only treat this as login failure
              // If the /bff/user endpoint returns 401, it means the user is not authenticated.
              // In this case, we want to redirect to the login page.
              // This can happen if the user's session has expired or they are not logged in.
              // On unauthorized, redirect to BFF login and preserve current route
              // so the user can return after successful authentication.
              // Prefer centralized redirect service when available.
              const returnUrl = window.top?.location?.pathname || window.location.pathname;
              if (this.authRedirect) {
                this.authRedirect.redirectToLogin(returnUrl);
              } else {
                window.top.location.href = `/bff/login?returnUrl=${returnUrl}`;
              }
            }
            return throwError(() => error);
          })
        );
    }

    // Non API-family traffic is untouched.
    return next.handle(req);
  }

  private isApiFamilyRequest(url: string): boolean {
    return [
      '/api/',
      '/ds5/',
      '/umn/',
      '/mdb/',
      '/akm/'
    ].some(prefix => this.hasPathPrefix(url, prefix));
  }

  private hasPathPrefix(url: string, prefix: string): boolean {
    return this.getRequestPath(url).startsWith(prefix);
  }

  private getRequestPath(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname || '';
    } catch {
      return (url || '').split('?')[0].split('#')[0];
    }
  }
}
